import { computed, reactive } from "vue";

const STORAGE_KEY = "xfwebsite-server-auth-v1";

const clone = (value) => JSON.parse(JSON.stringify(value));

const loadPersistedSession = () => {
  if (typeof window === "undefined") {
    return {
      token: "",
      user: null,
    };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        token: "",
        user: null,
      };
    }

    const parsed = JSON.parse(raw);
    return {
      token: parsed.token || "",
      user: parsed.user || null,
    };
  } catch (error) {
    return {
      token: "",
      user: null,
    };
  }
};

const persistedSession = loadPersistedSession();

const state = reactive({
  token: persistedSession.token,
  currentUser: persistedSession.user,
  users: [],
  sessionReady: !persistedSession.token,
  loadingSession: false,
});

let sessionPromise = null;

const persistSession = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      token: state.token,
      user: clone(state.currentUser),
    })
  );
};

const clearSession = () => {
  state.token = "";
  state.currentUser = null;
  state.users = [];
  state.sessionReady = true;

  if (typeof window !== "undefined") {
    window.localStorage.removeItem(STORAGE_KEY);
  }
};

const request = async (path, options = {}) => {
  try {
    const headers = {
      ...(options.headers || {}),
    };

    if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    if (state.token && options.skipAuth !== true) {
      headers.Authorization = `Bearer ${state.token}`;
    }

    const response = await fetch(path, {
      method: options.method || "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : null;

    if (!response.ok) {
      const errorMessage = payload?.error || "请求失败，请稍后重试。";

      if (response.status === 401 && options.skipAuth !== true) {
        clearSession();
      }

      return {
        ok: false,
        error: errorMessage,
        status: response.status,
      };
    }

    if (!contentType.includes("application/json")) {
      return {
        ok: false,
        error: "当前预览服务没有接上后台接口，请使用完整本地预览方式启动。",
        status: response.status,
      };
    }

    return {
      ok: true,
      data: payload,
      status: response.status,
    };
  } catch (error) {
    return {
      ok: false,
      error: "无法连接到本地后台服务，请先启动完整预览服务。",
      status: 0,
    };
  }
};

export const useAuthStore = () => {
  const ensureSessionLoaded = async () => {
    if (!state.token) {
      state.sessionReady = true;
      return {
        ok: false,
      };
    }

    if (state.sessionReady && state.currentUser) {
      return {
        ok: true,
        user: state.currentUser,
      };
    }

    if (sessionPromise) {
      return sessionPromise;
    }

    state.loadingSession = true;
    sessionPromise = request("/api/auth/me").then((result) => {
      if (result.ok) {
        state.currentUser = result.data.user;
        persistSession();
      } else {
        clearSession();
      }

      state.loadingSession = false;
      state.sessionReady = true;
      sessionPromise = null;
      return result;
    });

    return sessionPromise;
  };

  const login = async (username, password) => {
    const result = await request("/api/auth/login", {
      method: "POST",
      body: {
        username,
        password,
      },
      skipAuth: true,
    });

    if (!result.ok) {
      return result;
    }

    if (!result.data?.token || !result.data?.user) {
      return {
        ok: false,
        error: "登录接口返回异常，请确认本地后台服务已正确启动。",
      };
    }

    state.token = result.data.token;
    state.currentUser = result.data.user;
    state.sessionReady = true;
    persistSession();

    return {
      ok: true,
      user: result.data.user,
    };
  };

  const logout = async () => {
    if (state.token) {
      await request("/api/auth/logout", {
        method: "POST",
      });
    }

    clearSession();
  };

  const changeOwnPassword = async ({
    currentPassword,
    nextPassword,
    confirmPassword,
  }) => {
    const result = await request("/api/auth/change-password", {
      method: "POST",
      body: {
        currentPassword,
        nextPassword,
        confirmPassword,
      },
    });

    if (!result.ok) {
      return result;
    }

    state.currentUser = result.data.user;
    persistSession();
    return {
      ok: true,
      message: result.data.message,
    };
  };

  const fetchUsers = async () => {
    const result = await request("/api/admin/users");
    if (!result.ok) {
      return result;
    }

    state.users = result.data.users;
    return {
      ok: true,
      users: state.users,
    };
  };

  const addUser = async ({
    username,
    displayName,
    password,
    canAccessAdmin,
    isSuperAdmin,
  }) => {
    const result = await request("/api/admin/users", {
      method: "POST",
      body: {
        username,
        displayName,
        password,
        canAccessAdmin,
        isSuperAdmin,
      },
    });

    if (!result.ok) {
      return result;
    }

    await fetchUsers();
    return {
      ok: true,
      user: result.data.user,
    };
  };

  const updateUser = async (userId, patch) => {
    const result = await request(`/api/admin/users/${userId}`, {
      method: "PATCH",
      body: patch,
    });

    if (!result.ok) {
      return result;
    }

    await fetchUsers();

    if (state.currentUser?.id === userId) {
      state.currentUser = result.data.user;
      persistSession();
    }

    return {
      ok: true,
      user: result.data.user,
    };
  };

  const removeUser = async (userId) => {
    const result = await request(`/api/admin/users/${userId}`, {
      method: "DELETE",
    });

    if (!result.ok) {
      return result;
    }

    await fetchUsers();
    return {
      ok: true,
    };
  };

  return {
    token: computed(() => state.token),
    users: computed(() => state.users),
    currentUser: computed(() => state.currentUser),
    isLoggedIn: computed(() => Boolean(state.currentUser && state.token)),
    canAccessAdmin: computed(
      () => Boolean(state.currentUser?.canAccessAdmin || state.currentUser?.isSuperAdmin)
    ),
    isSuperAdmin: computed(() => Boolean(state.currentUser?.isSuperAdmin)),
    sessionReady: computed(() => state.sessionReady),
    loadingSession: computed(() => state.loadingSession),
    ensureSessionLoaded,
    login,
    logout,
    changeOwnPassword,
    fetchUsers,
    addUser,
    updateUser,
    removeUser,
  };
};
